import React, { useState, useEffect, useMemo } from "react";
import { useLoading } from "../Loading";
import { collection,  doc, getDoc, getDocs, onSnapshot, or, query, Timestamp, updateDoc, where } from "firebase/firestore";
import { useAuth } from "../../contexts/authContext";
import db from "../../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import MobileStepper from '@mui/material/MobileStepper';
import Button from '@mui/material/Button';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { CNav, CNavItem, CNavLink, CTabContent, CTabPane } from '@coreui/react'
import { cilXCircle, cilCheckCircle } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { DialogContent, DialogContentText } from "@mui/material";
import { Box } from "@mui/system";
import { finalApprove, rejectApplication } from "../../utils/api";

function FinalApproval() {
    const { setLoading } = useLoading();
    const { currentUser } = useAuth();
    const [appointmentData, setAppointmentData] = useState();
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [openReasonDialog, setOpenReasonDialog] = useState(false);
    const [reason, setReason] = useState("");
    const [search, setSearch] = useState();
    const [selectedAppointment, setSelectedAppointment] = useState();
    const [activeKey, setActiveKey] = useState(1);
    const [petNames, setPetName] = useState();
    const [filters, setFilters] = useState(
        {
            petType: "all",
            petName: "all",
        }
    );


    useEffect(() => {
        if (!currentUser) {
            navigate('/');
            return;
        }

        const today = new Date();
        const todayTimestamp = Timestamp.fromDate(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(),
            today.getUTCHours(), today.getUTCMinutes(), today.getUTCSeconds(), today.getUTCMilliseconds())));


        const appointmentRef = query(collection(db, "Appointments"),
            where("shelterId", "==", currentUser.uid), where("appointmentTime", "<", todayTimestamp));
        const unsubscribeFinalAppointment = onSnapshot(appointmentRef, (appointmentSnap) => {

            if (appointmentSnap.empty) {
                setAppointmentData([]);
                setLoading(false);
                return;
            }
            const appointmentList = appointmentSnap.docs.map((app) => ({
                appointmentId: app.id,
                ...app.data(),
            }));
            const petList = new Set();
            const updatedApplicationListPromise = appointmentList.map(async (app) => {
                let applicationData;
                try {
                    const applicationRef = doc(db, "Applications", app.appointmentId);
                    const applicationSnap = await getDoc(applicationRef);

                    let appointment = { ...app };
                    if (applicationSnap.exists()) {
                        const data = applicationSnap.data();
                        applicationData = { ...appointment, data };

                    }
                    const petRef = doc(db, "Pets", app.petId);
                    const petSnap = await getDoc(petRef);


                    if (petSnap.exists()) {
                        const { petName, petType } = petSnap.data();
                        petList.add(petName);
                        return { ...applicationData, petName: petName, petType: petType };
                    }
                    return { ...applicationData, petName: "", petType: "" };

                }
                catch (error) {
                    console.log(error);
                    return { ...applicationData, petName: "", petType: "" };
                }
            });

            Promise.all(updatedApplicationListPromise).then((updatedApplicationList) => {
                setAppointmentData(updatedApplicationList);
                console.log(updatedApplicationList);
                const nameToArray = Array.from(petList);
                setPetName(nameToArray);
                setLoading(false);
            }).catch((error) => {
                console.error(error);
                setLoading(false);
            });

        }, (error) => {
            console.error(error);
            setLoading(false);
        });

        return () => unsubscribeFinalAppointment();

    }, [currentUser, navigate, setPetName]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((pre) => ({
            ...pre,
            [name]: value,
        }));
    };

    const filteredAppointment = useMemo(() => {
        if (!appointmentData) {
            return [];
        }

        let filtered = appointmentData.filter((appointment) => {
            const appointmentName = appointment.data.email.toLowerCase();
            const searchTermLower = (search || "").toLowerCase();

            const typeMatch = filters.petType === "all" || appointment.petType === filters.petType;
            const nameMatch = filters.petName === "all" || appointment.petName === filters.petName;
            const searchMatch = appointmentName.includes(searchTermLower);
            const statusMatch =
                activeKey === 1
                    ? appointment.status === "Pending"
                    : activeKey === 2
                        ? appointment.status === "Approved"
                        : activeKey === 3
                            ? appointment.status === "Rejected"
                            : false;

            return typeMatch && searchMatch && statusMatch && nameMatch;
        });

        if (activeKey === 1 && filtered.length > 0) { // Pending
            filtered.sort((a, b) => a.appointmentTime.seconds - b.appointmentTime.seconds); // Nearest first
        } else if ((activeKey === 2 || activeKey === 3) && filtered.length > 0) { // Approved
            filtered.sort((a, b) => b.appointmentTime.seconds - a.appointmentTime.seconds); // Recently first
        }
        return filtered;
    }, [appointmentData, filters, search, activeKey]);

    const handleReject = async (appointment) => {
        setOpenReasonDialog(false);
        setLoading(true);
        if (appointment) {
            try {
                const appointmentRef = doc(db, "Appointments", appointment.appointmentId)
                await updateDoc(appointmentRef, { status: "Rejected", reason: reason });

                try {
                    await rejectApplication(appointment.adopterId, appointment.petName, reason);

                } catch (error) {
                    console.error("Error sending email:", error);
                }

                setAppointmentData(preData => {
                    return preData.map(doc => {
                        if (doc.appointmentId === appointment.appointmentId)
                            return { ...doc, status: "Rejected", reason: reason };
                        else
                            return doc;
                    });
                });
                setLoading(false);

            }
            catch (error) {
                setLoading(false);
                console.error(error);
            }

        }
        setLoading(false);
        setReason("");

    };

    const handleApprove = async (appointment) => {
        console.log(appointment);
        setLoading(true);
        if (appointment) {
            try {
                const appointmentRef = doc(db, "Appointments", appointment.appointmentId)
                await updateDoc(appointmentRef, { status: "Approved" });

                try {
                    await finalApprove(appointment.adopterId, appointment.petName);

                } catch (error) {
                    console.error("Error sending email:", error);
                }

                const petRef = doc(db, "Pets", appointment.petId);
                await updateDoc(petRef, { status: "Adopted" });

                // Reject other applications for the same pet
                const applicationsRef = collection(db, 'Applications');
                const q = query(applicationsRef, where('petId', '==', appointment.petId), where('adopterId', '!=', appointment.adopterId) );
                const querySnapshot = await getDocs(q);

                await Promise.all( querySnapshot.docs.map(async (docSnapshot) => {

                    if(docSnapshot&&docSnapshot.data().status==="Approved")
                        {
                            const appRef = doc(db, "Appointments", docSnapshot.id)
                            const appSnap = await getDoc(appRef);
        
                            if(!appSnap.exists())
                            {
                                await updateDoc(docSnapshot.ref, { status: 'Rejected', reason: `${appointment.petName} has been adopted by another adopter.` });
                                try {
                                    await rejectApplication(docSnapshot.data().adopterId, appointment.petName, `${appointment.petName} has been adopted by another adopter.`);
                
                                } catch (error) {
                                    console.error("Error sending email:", error);
                                }
                            }
                            else if(appSnap.exists() && (appSnap.data().status === "Pending"||appSnap.data().status === "Cancel"))
                            {
                                await updateDoc(docSnapshot.ref, { status: 'Rejected', reason: `${appointment.petName} has been adopted by another adopter.` });
                                await updateDoc(appRef, { status: 'Rejected', reason: `${appointment.petName} has been adopted by another adopter.` });
                                try {
                                    await rejectApplication(docSnapshot.data().adopterId, appointment.petName, `${appointment.petName} has been adopted by another adopter.`);
                
                                } catch (error) {
                                    console.error("Error sending email:", error);
                                }
                            }
                        }
                        else if(docSnapshot&&docSnapshot.data().status==="Pending"){
                            
                            await updateDoc(docSnapshot.ref, { status: 'Rejected', reason: `${appointment.petName} has been adopted by another adopter.` });
                            try {
                                await rejectApplication(docSnapshot.data().adopterId, appointment.petName, `${appointment.petName} has been adopted by another adopter.`);
            
                            } catch (error) {
                                console.error("Error sending email:", error);
                            }
                        }
                    
                }));


                window.location.reload();
                setLoading(false);

            }
            catch (error) {
                setLoading(false);
                console.error(error);
            }

        }

    };

    const handleClearFilters = () => {
        setFilters({
            petType: "all",
            petName: "all",
        })
        setSearch("");
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);

    };


    const deleteReason = (application) => {
        setSelectedAppointment(application);
        setOpenReasonDialog(true);
    };
    const viewAppointment = (appointment) => {
        setSelectedAppointment(appointment);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setOpenReasonDialog(false);
        setReason("");
    }

    const [activeStep, setActiveStep] = useState(0);
    const appointmentsPerPage = 10;
    const maxSteps = Math.ceil(filteredAppointment.length / appointmentsPerPage);
    const startIndex = activeStep * appointmentsPerPage;
    const currentAppointment = filteredAppointment.slice(startIndex, startIndex + appointmentsPerPage);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, maxSteps - 1));
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
    };

    return (
        <div className="applicationListContainer">
            <h2>Final Approval</h2>
            <CNav variant="tabs" role="tablist">
                <CNavItem>
                    <CNavLink active={activeKey === 1} onClick={() => setActiveKey(1)}>
                        Pending
                    </CNavLink>
                </CNavItem>
                <CNavItem>
                    <CNavLink active={activeKey === 2} onClick={() => setActiveKey(2)}>
                        Approved
                    </CNavLink>
                </CNavItem>
                <CNavItem>
                    <CNavLink active={activeKey === 3} onClick={() => setActiveKey(3)}>
                        Rejected
                    </CNavLink>
                </CNavItem>
            </CNav>
            <div className="filterContainer">
                <input type="text" className="searchBox" placeholder="Search by applicant email" value={search} onChange={handleSearchChange} />
                <div>
                    <label htmlFor="petType">Pet Types</label> <br></br>
                    <select name="petType" id="petType" className="form-select form-select-sm"
                        onChange={handleFilterChange} value={filters.petType}
                    >
                        <option value="all">All</option>
                        <option value="Dog">Dog</option>
                        <option value="Cat">Cat</option>
                        <option value="Other">Other</option>

                    </select>
                </div>
                {petNames && <div>
                    <label htmlFor="petName">Pet Name</label> <br></br>
                    <select name="petName" id="petName" className="form-select form-select-sm"
                        onChange={handleFilterChange} value={filters.petName}
                    >
                        <option value="all">All</option>
                        {petNames.map((name) =>

                            <option key={name} value={name}>{name}</option>
                        )

                        }

                    </select>
                </div>}

                <div className="clearBtnContainer">
                    {(filters.petType !== "all" || search || filters.petName !== "all") && <button onClick={handleClearFilters} className="clearFilterBtn" >
                        Clear
                    </button>}
                </div>
            </div>

            <CTabContent>
                <CTabPane role="tabpanel" aria-labelledby="home-tab" visible={true}>
                    <table>
                        <thead >
                            <tr>
                                <th>Applicant Name</th>
                                <th>Contact</th>
                                <th>Pet Name</th>
                                <th>Pet Type</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Time</th>
                                <th>Details</th>
                                {activeKey === 1 && <th>Actions</th>}
                                {activeKey === 3 && <th>Reasons</th>}

                            </tr>
                        </thead>
                        <tbody>
                            {currentAppointment.map((application) => (
                                <tr key={application.appoinmentId}>
                                    <td>{application.data.name}</td>
                                    <td>{application.data.phoneNumber}<br></br>{application.data.email}</td>
                                    <td>{application.petName}</td>
                                    <td>{application.petType}</td>
                                    <td>{application.date}</td>
                                    <td>{application.time}</td>
                                    <td>{application.status}</td>
                                    <td>
                                        <button
                                            className="viewApplication"
                                            onClick={() => viewAppointment(application)}
                                        >
                                            View
                                        </button>
                                    </td>
                                    {
                                        application.status === "Pending" && <td>
                                            <div style={{ display: "flex", justifySelf: "right", gap: "35px", width: "100%" }}>
                                                <CIcon icon={cilCheckCircle} size="xxl"
                                                    className="approve"
                                                    onClick={() => handleApprove(application)}>

                                                </CIcon>
                                                <CIcon icon={cilXCircle} size="xxl"
                                                    className="reject"
                                                    onClick={() => deleteReason(application)}>

                                                </CIcon>
                                            </div>
                                        </td>
                                    }
                                    {activeKey === 3 && <td>{application.reason}</td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CTabPane>
            </CTabContent>


            <Box sx={{ height: 60, maxWidth: 400, width: '100%', p: 2, margin: "auto" }}>
                <MobileStepper
                    variant="text"
                    steps={maxSteps}
                    position="static"
                    activeStep={activeStep}
                    nextButton={
                        <Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1 || maxSteps === 0}>
                            Next <KeyboardArrowRight />
                        </Button>
                    }
                    backButton={
                        <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                            <KeyboardArrowLeft /> Back
                        </Button>
                    }
                />
            </Box>
            {selectedAppointment && <Dialog style={{ zIndex: "99999" }}
                open={openDialog}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {"Applicant Details"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <table>
                            <tbody>
                                <tr display="flex" gap={10}>
                                    <td >Name:</td>
                                    <td >{selectedAppointment.data.name}</td>

                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >IC Number:</td>
                                    <td >{selectedAppointment.data.icNumber}</td>

                                </tr>
                                <tr display="flex" gap={12}>
                                    <td >Age:</td>
                                    <td >{selectedAppointment.data.age}</td>
                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >Email:</td>
                                    <td >{selectedAppointment.data.email}</td>
                                </tr>
                                <tr display="flex" gap={9}>
                                    <td >Gender:</td>
                                    <td >{selectedAppointment.data.gender}</td>
                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >Phone Number:</td>
                                    <td >{selectedAppointment.data.phoneNumber}</td>
                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >Address:</td>
                                    <td >{selectedAppointment.data.address}, {selectedAppointment.data.postcode} {selectedAppointment.data.city}, {selectedAppointment.data.state}.</td>
                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >Who adopted for:</td>
                                    <td >{selectedAppointment.data.whoAdoptfor}</td>
                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >No. of Adults:</td>
                                    <td >{selectedAppointment.data.noOfAdults}</td>
                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >Had pet before:</td>
                                    <td >{selectedAppointment.data.hadPet}</td>
                                </tr>
                                {selectedAppointment.data.hadPet === "Yes" &&
                                    <tr display="flex" gap={10}>
                                        <td >No. of pets:</td>
                                        <td >{selectedAppointment.data.noOfOtherPets}</td>
                                    </tr>}
                                <tr display="flex" gap={10}>
                                    <td >Had surrendered pet:</td>
                                    <td >{selectedAppointment.data.hadSurrendered}</td>
                                </tr>
                                {selectedAppointment.data.hadSurrendered === "Yes" &&
                                    <tr display="flex" gap={10}>
                                        <td >Surrendered Reason:</td>
                                        <td >{selectedAppointment.data.surrenderedReason}</td>
                                    </tr>
                                }

                                <tr display="flex" gap={10}>
                                    <td >Type of Home:</td>
                                    <td >{selectedAppointment.data.typeOfHome}</td>
                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >Anybody Allergic:</td>
                                    <td >{selectedAppointment.data.petAllergic}</td>
                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >Main Caregiver:</td>
                                    <td >{selectedAppointment.data.mainCaregiver}</td>
                                </tr>
                                <tr display="flex" gap={10}>
                                    <td >Caregiver Email:</td>
                                    <td >{selectedAppointment.data.emailofCaregiver}</td>
                                </tr>

                            </tbody>
                        </table>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>}
            {selectedAppointment && <Dialog style={{ zIndex: "9999" }}
                open={openReasonDialog}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {"Reason for rejection"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} required
                            style={{ border: "1px solid #ccc", borderRadius: "5px", padding: "10px", width: "300px" }}></input>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={() => handleReject(selectedAppointment)} disabled={!reason.trim()} autoFocus>
                        Save </Button>
                </DialogActions>
            </Dialog>}
        </div >
    );
}

export default FinalApproval;